import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const money = (v) => `$${(v || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Kpi = ({ label, value, id, to, tone = "text-[#0B2D5B]" }) => (
  <Link to={to} className="min-w-0 rounded-xl bg-[#E9EEF2]/60 p-3 transition-colors hover:bg-[#E9EEF2]" data-testid={`kpi-${id}`}>
    <p className="text-[11px] font-semibold uppercase leading-tight tracking-wider text-slate-500">{label}</p>
    <p className={`font-mono-num mt-1 text-xl font-bold ${tone}`} data-testid={`kpi-${id}-value`}>{value}</p>
  </Link>
);

const People = ({ title, rows, id, render }) => rows.length > 0 && (
  <div className="mt-4" data-testid={`kpi-list-${id}`}>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
    <ul className="mt-1 divide-y divide-slate-100 text-sm">{rows.map((r) => <li key={r.membership_id} className="flex justify-between gap-2 py-1.5"><span className="truncate">{r.name}</span><span className="shrink-0 text-xs text-slate-500">{render(r)}</span></li>)}</ul>
  </div>
);

const ExpenseCard = () => {
  const tz = useAuth().business?.timezone;
  const [s, setS] = useState(null);
  useEffect(() => {
    const d = new Date(`${new Intl.DateTimeFormat("en-CA", { timeZone: tz || undefined, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date())}T00:00`);
    const m = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    api.get("/expenses/summary", { params: { start: iso(m), end: iso(new Date(m.getFullYear(), m.getMonth(), m.getDate() + 6)) } }).then((r) => setS(r.data.totals)).catch(() => {});
  }, [tz]);
  if (!s) return null;
  return (
    <Link to="/app/expenses" className="block rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#00C16B] sm:p-6" data-testid="dashboard-expenses">
      <h2 className="font-semibold text-[#0B2D5B]">Cleaning & maintenance this week</h2>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        {[["Cleaning", s.cleaning, "text-[#00A85C]"], ["Maintenance", s.maintenance, "text-[#E07800]"], ["Combined", s.total, "text-[#0B2D5B]"], ["Unpaid", s.unpaid, s.unpaid ? "text-[#B35F00]" : "text-[#0B2D5B]"]].map(([l, v, t]) => (
          <div key={l} className="min-w-0 rounded-xl bg-[#E9EEF2]/60 p-3"><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{l}</p><p className={`font-mono-num mt-1 text-lg font-bold ${t}`} data-testid={`dashboard-exp-${l.toLowerCase()}`}>{money(v)}</p></div>))}
      </div>
    </Link>
  );
};

export const OwnerStats = () => {
  const { business } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/dashboard/summary").then((r) => setD(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [business?.business_id]);
  if (!d) return <div className="h-64 animate-pulse rounded-2xl bg-white" data-testid="attendance-kpis-loading" />;
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="attendance-kpis">
        <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="font-semibold text-[#0B2D5B]">Today's attendance</h2><Link to="/app/live-attendance" className="text-sm font-semibold text-[#00A85C]" data-testid="kpi-live-link">Live attendance</Link></div>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
          <Kpi id="working" label="Working now" value={d.working} to="/app/live-attendance" tone="text-[#00A85C]" />
          <Kpi id="on-break" label="On break" value={d.on_break} to="/app/live-attendance" tone="text-[#E07800]" />
          <Kpi id="clocked-out" label="Clocked out" value={d.clocked_out} to="/app/live-attendance" />
          <Kpi id="late" label="Late arrivals" value={d.late.length} to="/app/live-attendance" tone={d.late.length ? "text-[#E07800]" : undefined} />
          <Kpi id="missing" label="Missing clock-ins" value={d.missing.length} to="/app/live-attendance" tone={d.missing.length ? "text-red-600" : undefined} />
          <Kpi id="missed-out" label="Missed clock-outs" value={d.missed_clock_outs} to="/app/live-attendance" tone={d.missed_clock_outs ? "text-red-600" : undefined} />
          <Kpi id="hours-today" label="Hours today" value={d.hours_today.toFixed(1)} to="/app/timesheets" />
          {d.show_cost && <Kpi id="cost-today" label="Labour cost today" value={money(d.cost_today)} to="/app/reports" />}
          {d.show_cost && <Kpi id="cost-week" label="Labour cost this week" value={money(d.cost_week)} to="/app/reports" />}
          <Kpi id="to-approve" label="Timesheets to approve" value={d.timesheets_to_approve} to="/app/timesheets" />
          <Kpi id="pending-leave" label="Leave to review" value={d.pending_leave} to="/app/leave" />
          <Kpi id="pending-staff" label="Staff to approve" value={d.pending_staff} to="/app/employees" tone={d.pending_staff ? "text-[#E07800]" : undefined} />
        </div>
        <People id="missing" title="Not clocked in yet" rows={d.missing} render={(r) => `${r.start} shift${r.running_late ? ` · running ${r.running_late.minutes} min late` : ""}`} />
        <People id="late" title="Arrived late" rows={d.late} render={(r) => `${r.minutes} min after ${r.start}`} />
      </div>
      <ExpenseCard />
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="labour-chart">
        <h2 className="font-semibold text-[#0B2D5B]">{d.show_cost ? "Labour cost this week" : "Hours worked this week"}</h2>
        <p className="text-sm text-slate-500">{d.hours_week.toFixed(1)} hours{d.show_cost ? ` · ${money(d.cost_week)} at hourly rates` : ""}</p>
        <div className="mt-4 h-56 w-full"><ResponsiveContainer width="100%" height="100%">
          <BarChart data={d.daily} margin={{ left: -10, right: 4 }}><XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} /><YAxis tickLine={false} axisLine={false} fontSize={11} width={48} />
            <Tooltip formatter={(v, k) => (k === "cost" ? money(v) : `${v} h`)} cursor={{ fill: "#E9EEF2" }} /><Bar dataKey={d.show_cost ? "cost" : "hours"} fill="#00C16B" radius={[6, 6, 0, 0]} /></BarChart>
        </ResponsiveContainer></div>
        {d.departments.length > 0 && (
          <ul className="mt-4 space-y-2" data-testid="labour-departments">{d.departments.map((x) => (
            <li key={x.name} className="flex items-center justify-between gap-2 text-sm"><span className="flex min-w-0 items-center gap-2"><span className="h-3 w-3 shrink-0 rounded-full" style={{ background: x.color }} /><span className="truncate">{x.name}</span></span><span className="font-mono-num shrink-0 text-slate-600">{x.hours.toFixed(1)} h{x.cost != null ? ` · ${money(x.cost)}` : ""}</span></li>
          ))}</ul>
        )}
      </div>
    </div>
  );
};
